import type { ColumnVisibilityState, RowData } from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StrictColumnDef } from '../components/tanstack-table/column';

// Preserve user reorderings for columns in both lists, insert new columns at their
// canonical position (right after their nearest preceding sibling in newColumnNames),
// and drop columns that no longer exist.
export function mergeColumnOrder(existingOrder: string[], newColumnNames: string[]): string[] {
  const existingSet = new Set(existingOrder);
  const newSet = new Set(newColumnNames);
  const merged = existingOrder.filter((id) => newSet.has(id));
  for (let i = 0; i < newColumnNames.length; i++) {
    const id = newColumnNames[i];
    if (existingSet.has(id)) {
      continue;
    }

    let insertAt = 0;
    for (let j = i - 1; j >= 0; j--) {
      const prevIndex = merged.indexOf(newColumnNames[j]);
      if (prevIndex !== -1) {
        insertAt = prevIndex + 1;
        break;
      }
    }

    merged.splice(insertAt, 0, id);
  }

  return merged;
}

/**
 * True when the current column visibility and order both match their defaults
 * — i.e. when `resetColumns()` would be a no-op.
 */
export function isColumnLayoutDefault(
  hiddenColumnNames: string[],
  orderedColumnNames: string[],
  defaultHiddenColumnIds: string[],
  allColumnNames: string[]
): boolean {
  const defaultHidden = new Set(defaultHiddenColumnIds);
  const currentHidden = new Set(hiddenColumnNames);
  const hiddenMatches =
    defaultHidden.size === currentHidden.size &&
    [...currentHidden].every((id) => defaultHidden.has(id));
  const orderMatches =
    orderedColumnNames.length === allColumnNames.length &&
    orderedColumnNames.every((id, index) => id === allColumnNames[index]);

  return hiddenMatches && orderMatches;
}

function resolveColumnOrder(stored: string[] | undefined, current: string[]): string[] {
  if (!stored) {
    return current;
  }

  return mergeColumnOrder(stored, current);
}

type ColumnNamesState = {
  allTableColumnNames: Record<string, string[]>;
  hiddenTableColumnNames: Record<string, string[]>;
  columnOrder: Record<string, string[]>;
  // Actions
  setHiddenTableColumnNames: (tableKey: string, columnNames: string[]) => void;
  setAllTableColumnNames: (tableKey: string, columnNames: string[]) => void;
  setColumnOrder: (tableKey: string, columnIds: string[]) => void;
};

const useColumnVisibilityStore = create<ColumnNamesState>()(
  persist(
    (set) => ({
      hiddenTableColumnNames: {},
      setHiddenTableColumnNames: (tableKey: string, columnNames: string[]) =>
        set((state) => ({
          hiddenTableColumnNames: {
            ...state.hiddenTableColumnNames,
            [tableKey]: columnNames,
          },
        })),
      allTableColumnNames: {},
      setAllTableColumnNames: (tableKey: string, columnNames: string[]) =>
        set((state) => ({
          allTableColumnNames: {
            ...state.allTableColumnNames,
            [tableKey]: columnNames,
          },
        })),
      columnOrder: {},
      setColumnOrder: (tableKey: string, columnIds: string[]) =>
        set((state) => ({
          columnOrder: { ...state.columnOrder, [tableKey]: columnIds },
        })),
    }),
    {
      name: 'hidden-columns-store',
    }
  )
);

export type TableColumnConfig = {
  pinnedColumns?: string[];
  defaultHiddenColumnIds?: string[];
};

type VisibleColumnsResult<T extends RowData> = {
  /** All columns (including hidden) — pass to TanStackTable so sorting works on all columns */
  columns: StrictColumnDef<T>[];
  /** Only visible columns — use for layout/sizing outside the table */
  visibleColumns: StrictColumnDef<T>[];
  /** TanStack visibility state — pass to TanStackTable */
  columnVisibility: ColumnVisibilityState;
  /** TanStack columnOrder state — ordered array of all column IDs */
  columnOrder: string[];
};

export function useVisibleColumns<T extends RowData>(
  allColumns: StrictColumnDef<T>[],
  tableKey: string,
  config?: TableColumnConfig
): VisibleColumnsResult<T> {
  const {
    hiddenTableColumnNames,
    setAllTableColumnNames,
    setHiddenTableColumnNames,
    setColumnOrder,
    columnOrder: storedColumnOrder,
  } = useColumnVisibilityStore();
  const hiddenColumnNames = hiddenTableColumnNames[tableKey] ?? [];
  const previousColumnNamesRef = useRef<string[]>([]);

  useEffect(() => {
    const newColumnNames = allColumns.map((column) => column.id);
    // Only update if the IDs actually changed
    const previousNames = previousColumnNamesRef.current;
    if (
      newColumnNames.length !== previousNames.length ||
      newColumnNames.some((id, index) => id !== previousNames[index])
    ) {
      // Read current store state imperatively to avoid dependency-array loops
      const storeState = useColumnVisibilityStore.getState();
      const knownNames = storeState.allTableColumnNames[tableKey] ?? [];
      const currentHidden = storeState.hiddenTableColumnNames[tableKey] ?? [];

      // Columns appearing for the first time that should start hidden
      const toHideByDefault = (config?.defaultHiddenColumnIds ?? []).filter(
        (id) => !knownNames.includes(id) && !currentHidden.includes(id)
      );

      if (toHideByDefault.length > 0) {
        setHiddenTableColumnNames(tableKey, [...currentHidden, ...toHideByDefault]);
      }

      setAllTableColumnNames(tableKey, newColumnNames);

      // Initialize or update column order
      const existingOrder = storeState.columnOrder[tableKey];
      if (!existingOrder) {
        setColumnOrder(tableKey, newColumnNames);
      } else {
        const merged = mergeColumnOrder(existingOrder, newColumnNames);
        if (
          merged.length !== existingOrder.length ||
          merged.some((id, i) => id !== existingOrder[i])
        ) {
          setColumnOrder(tableKey, merged);
        }
      }

      previousColumnNamesRef.current = newColumnNames;
    }
  }, [
    allColumns,
    setAllTableColumnNames,
    setHiddenTableColumnNames,
    setColumnOrder,
    tableKey,
    config?.defaultHiddenColumnIds,
  ]);

  const visibleColumns = useMemo(() => {
    return allColumns.filter((column) => !hiddenColumnNames.includes(column.id));
  }, [allColumns, hiddenColumnNames]);

  const columnVisibility = useMemo(() => {
    const visibility: ColumnVisibilityState = {};
    for (const name of hiddenColumnNames) {
      visibility[name] = false;
    }

    return visibility;
  }, [hiddenColumnNames]);

  const allColumnIds = useMemo(() => allColumns.map((col) => col.id), [allColumns]);
  const columnOrder = useMemo(
    () => resolveColumnOrder(storedColumnOrder[tableKey], allColumnIds),
    [storedColumnOrder, tableKey, allColumnIds]
  );

  return { columns: allColumns, visibleColumns, columnVisibility, columnOrder };
}

export type ColumnState = {
  /** Column id — also shown verbatim as the row's label in the picker. */
  id: string;
  enabled: boolean;
  pinned?: boolean;
};

export function useEditVisibleColumns(
  tableKey: string,
  pinnedColumns?: string[],
  defaultHiddenColumnIds?: string[]
) {
  const {
    hiddenTableColumnNames,
    setHiddenTableColumnNames,
    allTableColumnNames,
    columnOrder: storedColumnOrder,
    setColumnOrder,
  } = useColumnVisibilityStore();
  const hiddenColumnNames = hiddenTableColumnNames[tableKey] ?? [];
  const allColumnNames = allTableColumnNames[tableKey] || [];
  const pinnedSet = new Set(pinnedColumns ?? []);

  const orderedColumnNames = useMemo(
    () => resolveColumnOrder(storedColumnOrder[tableKey], allColumnNames),
    [storedColumnOrder, tableKey, allColumnNames]
  );

  const isDefault = isColumnLayoutDefault(
    hiddenColumnNames,
    orderedColumnNames,
    defaultHiddenColumnIds ?? [],
    allColumnNames
  );

  const columnStates: ColumnState[] = orderedColumnNames.map((id) => ({
    id,
    enabled: !hiddenColumnNames.includes(id),
    pinned: pinnedSet.has(id),
  }));

  const toggleColumn = (id: string) => {
    if (pinnedSet.has(id)) {
      return;
    }

    const newHiddenColumns = hiddenColumnNames.includes(id)
      ? hiddenColumnNames.filter((name) => name !== id)
      : [...hiddenColumnNames, id];
    setHiddenTableColumnNames(tableKey, newHiddenColumns);
  };

  const reorderColumns = (orderedIds: string[]) => {
    setColumnOrder(tableKey, orderedIds);
  };

  const resetColumns = () => {
    setHiddenTableColumnNames(tableKey, defaultHiddenColumnIds ?? []);
    setColumnOrder(tableKey, allColumnNames);
  };

  return { columnStates, toggleColumn, reorderColumns, resetColumns, isDefault };
}
